import React from "react";
import Card from "features/shared/components/card/Card";
import CommodityIcon from "features/shared/components/icons/CommodityIcon";
import { formatDate, formatPrice } from "features/shared/helpers/Formatters";
import { DepositAgreementDTO } from "../dtos/DepositAgreementDTO";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DepositAgreementStatusLabel from "./DepositAgreementStatusLabel";
import DocumentDownloadButton from "features/shared/components/documents/DocumentDownloadButton";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import { useTranslation } from "react-i18next";
import ContractLink from "features/invoices/components/ContractLink";
import EntityLinkDTO from "features/invoices/dtos/EntityLinkDTO";
import { Entity } from "features/shared/enums/entity";
import InfoItemMobile from "features/shared/components/infoItem/InfoItemMobile";

type DepositAgreementItem = NonNullable<DepositAgreementDTO["result"]>[number];

interface Props { depositAgreement: DepositAgreementItem; }

const DepositAgreementCard = ({ depositAgreement }: Props) => {
  const t = useTranslation("depositAgreement\\depositAgreement").t;

  const renderDate = (iso?: string, icon?: "calendar" | "calendar-check") =>
    iso ? (
      <span className="small d-inline-flex align-items-center text-nowrap">
        {icon && <FontAwesomeIcon icon={icon} size="xs" fixedWidth />}
        <time className="ml-1" dateTime={iso}>{formatDate(iso)}</time>
      </span>
    ) : <span>-</span>;

  const amount = formatPrice(depositAgreement.depositAgreementAmount!, depositAgreement.deposit!.currency!, 2);
  const used   = depositAgreement.deposit?.amount == null ? "-": formatPrice(depositAgreement.deposit!.amount!, depositAgreement.deposit!.currency!, 2);

  const singleDoc: DocumentSimplifiedDTO | undefined = (depositAgreement as any)?.documents ?? undefined;

  const docs: DocumentSimplifiedDTO[] = singleDoc ? [singleDoc] : [];
  const docsCount = docs.length;
  const documentIcon: IconProp = "file-pdf";

  const contractId: string | undefined =
    (depositAgreement as any)?.contractId ?? (depositAgreement as any)?.contract?.contractId;

  const contractValue = (
    <div className="d-flex align-items-center w-100 overflow-hidden" style={{ minWidth: 0 }}>
      <CommodityIcon commodityDetail={depositAgreement.commodityDetail!} includeColor />
      <div className="ml-2 w-100 overflow-hidden" style={{ minWidth: 0 }}>
        {(() => {
          const number = depositAgreement.contractNumber ?? undefined;
          if (!contractId || !number) return "-";
          const link: EntityLinkDTO = { entity: Entity.Contract, entityId: contractId, name: number };
          return (
            <span title={number} className="d-block">
              <ContractLink entityLink={link} smallText={false} className="text-truncate" />
            </span>
          );
        })()}
      </div>
    </div>
  );

  const agreementValue = (
    <div className="text-truncate d-block w-100" title={depositAgreement.depositAgreementNumber ?? "-"}>
      {depositAgreement.depositAgreementNumber ?? "-"}
    </div>
  );

  const statusValue = (
    <span title={depositAgreement.depositAgreementStatus!.toString()}>
      <DepositAgreementStatusLabel status={depositAgreement.depositAgreementStatus!} />
    </span>
  );

  const downloadId = String(
    depositAgreement.depositAgreementNumber ?? depositAgreement.contractNumber ?? "deposit-agreement"
  );

  const V = (key: string, node: React.ReactNode,
    {alwaysShowTitle = false, valueCls = "", titleCls = "", smTitleCls = "", smValueCls = "",
    }: {
      alwaysShowTitle?: boolean;
      valueCls?: string;
      titleCls?: string;
      smTitleCls?: string;
      smValueCls?: string;
    } = {}
  ) => (
    <>
      <div className={alwaysShowTitle ? "d-block d-sm-none" : undefined}>
        <InfoItemMobile
          title={t(key)}
          value={node as any}
          hideTitleOn="sm"
          valueClassName={`text-right text-sm-left ${valueCls}`}
          titleClassName={titleCls}
        />
      </div>
      {alwaysShowTitle && (
        <div className={`d-none d-sm-flex ${valueCls}`}>
          <div className={`text-muted pr-1 ${titleCls} ${smTitleCls}`}>{t(key)}</div>
          <div className={smValueCls}>{node}</div>
        </div>
      )}
    </>
  );


  return (
    <Card className="px-3 py-2 mb-2">
      <div className="row no-gutters align-items-center">
        <div className="col-12 col-sm-11 col-md-11 col-lg-10 col-xl-10">
          <div className="row no-gutters align-items-center">
            <div className="col-12 col-sm-3 col-md-3 col-lg-3">
              {V("header.contractNumber", contractValue)}
            </div>

            <div className="col-12 col-sm-4 col-md-4 col-lg-3">
              {V("header.depositAgreementNumber", agreementValue)}
              <div className="d-block d-sm-none">
                {V("header.status", statusValue)}
              </div>
              <div className="d-none d-sm-block d-lg-none">{statusValue}</div>
            </div>

            <div className="col-12 col-sm-3 col-md-3 col-lg-3">
              {V("header.issueDate", renderDate(depositAgreement.issueDate, "calendar-check"))}
              {V("header.dueDate",   renderDate(depositAgreement.dueDate,   "calendar"))}
            </div>

            <div className="col-12 col-sm-2 col-md-2 col-lg-3">
              {V("header.amount", <span className="text-truncate d-block" title={amount}>{amount}</span>)}
              {V("header.used",   <span className="text-truncate d-block text-muted" title={used}>{used}</span>, { alwaysShowTitle: true, smTitleCls: "small", smValueCls: "small" })}
            </div>
          </div>
        </div>

        <div className="d-none d-lg-flex col-lg-1 col-xl-1 align-items-center">{statusValue}</div>

        <div className="col-12 col-sm-1 col-md-1 col-lg-1 col-xl-1">
          {V(
            "header.download",
            docsCount > 0 ? (
              <DocumentDownloadButton id={downloadId} icon={documentIcon} documents={docs} wrapText />
            ) : (
              <span className="d-inline d-sm-none">-</span>
            ),
            { valueCls: "d-flex justify-content-end justify-content-lg-end w-100", titleCls: "text-nowrap" }
          )}
        </div>
      </div>
    </Card>
  );
};

export default DepositAgreementCard;
